import React, { useEffect, useState, useContext } from "react";
import { useParams } from 'react-router-dom';
import { UserContext } from "../UserContext";
import { toast, ToastContainer, Zoom } from "react-toastify";
import Header from "../Header";
import "./index.css";

const OperatorPlanMapping = () => {
  const { id } = useParams();
  const { user, token } = useContext(UserContext);
  const [operators, setOperators] = useState([]);
  const [plans, setPlans] = useState([]);
  const [selectedOperator, setSelectedOperator] = useState("");
  const [mappedPlans, setMappedPlans] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [bulkAmount, setBulkAmount] = useState("");
  const [selectedDuration, setSelectedDuration] = useState('All');

  // ✅ Toggle row expansion
  const toggleRowExpansion = (planId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(planId)) {
        newSet.delete(planId);
      } else {
        newSet.add(planId);
      }
      return newSet;
    });
  };

  useEffect(() => {
    setSelectedOperator(id);
  }, [id]);

  // ✅ Fetch operators
  const fetchOperators = async () => {
    try {
      const res = await fetch("https://gsrtv.in/portal_php/operator_api.php", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.status === "success") {
        setOperators(data.operators);
      }
    } catch (err) {
      toast.error("Failed to load operators");
    }
  };

  // ✅ Fetch all plans
  const fetchPlans = async () => {
    try {
      const res = await fetch("https://gsrtv.in/portal_php/get_operator_plans_with_apps.php", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (Array.isArray(data)) setPlans(data);
    } catch (err) {
      toast.error("Failed to load plans");
    }
  };

  // ✅ Fetch existing mappings for selected operator
  const fetchOperatorMapping = async (opId) => {
    if (!opId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `https://gsrtv.in/portal_php/operator_plan_mapping.php?operator_id=${opId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();

      if (data.status === "success" && Array.isArray(data.mappings)) {
        const map = {};
        data.mappings.forEach((m) => {
          map[m.plan_id] = { checked: true, price: m.planPrice };
        });
        setMappedPlans(map);
      } else {
        setMappedPlans({});
      }
    } catch (err) {
      toast.error("Failed to load operator mapping");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Handle toggle - Auto-fill price for admin users
  const togglePlan = (planId) => {
    setMappedPlans((prev) => {
      const current = { ...prev };
      
      if (current[planId]) {
        // If plan is already selected, deselect it
        delete current[planId];
      } else {
        // If plan is being selected, auto-fill price for admin users
        const plan = plans.find(p => p.id === parseInt(planId));
        if (plan && user?.role === "admin") {
          const basePrice = parseFloat(plan.planPrice);
          let customPrice = basePrice;
          
          // Add bulk amount if specified
          if (bulkAmount && parseInt(bulkAmount) > 0) {
            customPrice = basePrice + parseInt(bulkAmount);
          }
          
          current[planId] = { 
            checked: true, 
            price: customPrice.toString() 
          };
        } else {
          current[planId] = { checked: true, price: "" };
        }
      }
      return current;
    });
  };

  // ✅ Handle custom price change
  const handlePriceChange = (planId, value) => {
    setMappedPlans((prev) => ({
      ...prev,
      [planId]: { ...(prev[planId] || {}), price: value },
    }));
  };

  // ✅ Select All plans - Auto-fill prices for admin users
  const handleSelectAll = () => {
    const allPlansMap = {};
    plans.forEach(plan => {
      if (user?.role === "admin") {
        const basePrice = parseFloat(plan.planPrice);
        let customPrice = basePrice;
        
        // Add bulk amount if specified
        if (bulkAmount && parseInt(bulkAmount) > 0) {
          customPrice = basePrice + parseInt(bulkAmount);
        }
        
        allPlansMap[plan.id] = { 
          checked: true, 
          price: customPrice.toString() 
        };
      } else {
        allPlansMap[plan.id] = { checked: true, price: "" };
      }
    });
    setMappedPlans(allPlansMap);
  };

  // ✅ Deselect All plans
  const handleDeselectAll = () => {
    setMappedPlans({});
    setBulkAmount("");
  };

  // ✅ Apply bulk amount to all selected plans
  const applyBulkAmount = () => {
    if (!bulkAmount || bulkAmount === "" || isNaN(parseInt(bulkAmount))) {
      toast.warn("Please enter a valid amount");
      return;
    }

    const amount = parseInt(bulkAmount);
    if (amount < 0) {
      toast.warn("Amount cannot be negative");
      return;
    }

    const selectedPlanIds = Object.keys(mappedPlans);
    if (selectedPlanIds.length === 0) {
      toast.warn("No plans selected");
      return;
    }

    const updatedMappings = { ...mappedPlans };
    
    selectedPlanIds.forEach(planId => {
      const plan = plans.find(p => p.id === parseInt(planId));
      if (plan) {
        const basePrice = parseFloat(plan.planPrice);
        const newPrice = basePrice + amount;
        updatedMappings[planId] = {
          ...updatedMappings[planId],
          price: newPrice.toString()
        };
      }
    });

    setMappedPlans(updatedMappings);
    toast.success(`Added ₹${amount} to ${selectedPlanIds.length} plan(s)`);
  };

  // ✅ Reset bulk amount for all selected plans
  const resetBulkAmount = () => {
    const selectedPlanIds = Object.keys(mappedPlans);
    if (selectedPlanIds.length === 0) {
      toast.warn("No plans selected");
      return;
    }

    const updatedMappings = { ...mappedPlans };
    
    selectedPlanIds.forEach(planId => {
      const plan = plans.find(p => p.id === parseInt(planId));
      if (plan && user?.role === "admin") {
        // For admin users, reset to base price (not empty)
        const basePrice = parseFloat(plan.planPrice);
        updatedMappings[planId] = {
          ...updatedMappings[planId],
          price: basePrice.toString()
        };
      } else {
        updatedMappings[planId] = {
          ...updatedMappings[planId],
          price: ""
        };
      }
    });

    setMappedPlans(updatedMappings);
    setBulkAmount("");
    toast.success(`Reset prices for ${selectedPlanIds.length} plan(s)`);
  };

  // ✅ Auto-update prices when bulk amount changes for admin users
  useEffect(() => {
    if (user?.role === "admin" && bulkAmount !== "" && Object.keys(mappedPlans).length > 0) {
      const amount = parseInt(bulkAmount) || 0;
      const updatedMappings = { ...mappedPlans };
      let updatedCount = 0;

      Object.keys(updatedMappings).forEach(planId => {
        const plan = plans.find(p => p.id === parseInt(planId));
        if (plan) {
          const basePrice = parseFloat(plan.planPrice);
          const newPrice = basePrice + amount;
          updatedMappings[planId] = {
            ...updatedMappings[planId],
            price: newPrice.toString()
          };
          updatedCount++;
        }
      });

      if (updatedCount > 0) {
        setMappedPlans(updatedMappings);
      }
    }
  }, [bulkAmount]); // Only run when bulkAmount changes

  // ✅ Check if all plans are selected
  const allPlansSelected = plans.length > 0 && Object.keys(mappedPlans).length === plans.length;

  // ✅ Save mapping
  const handleSaveMapping = async () => {
    if (!selectedOperator) {
      toast.warn("Please select an operator");
      return;
    }

    const mappings = Object.keys(mappedPlans).map((planId) => ({
      plan_id: parseInt(planId),
      planPrice: mappedPlans[planId].price ? parseFloat(mappedPlans[planId].price) : null,
    }));

    setSaving(true);
    try {
      const res = await fetch("https://gsrtv.in/portal_php/operator_plan_mapping.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          operator_id: selectedOperator,
          mappings,
        }),
      });

      const data = await res.json();      
      if (data.status === "success") {
        toast.success("Mappings saved successfully!");
      } else {
        toast.error(data.message || "Failed to save mappings");
      }
    } catch (err) {
      toast.error("Server error while saving");
    } finally {
      setSaving(false);
    }
  };

  // Calculate total additional amount for selected plans
  const calculateTotalAdditionalAmount = () => {
    let total = 0;
    Object.keys(mappedPlans).forEach(planId => {
      const plan = plans.find(p => p.id === parseInt(planId));
      if (plan && mappedPlans[planId].price) {
        const basePrice = parseFloat(plan.planPrice);
        const customPrice = parseFloat(mappedPlans[planId].price);
        total += (customPrice - basePrice);
      }
    });
    return total;
  };

  // Check if user is admin
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    fetchOperators();
    fetchPlans();
  }, []);

  useEffect(() => {
    if (selectedOperator) {
      fetchOperatorMapping(selectedOperator);
    }
  }, [selectedOperator]);

  
  const filteredPackages = selectedDuration === 'All'
      ? plans
      : plans.filter(pack => Number(pack.duration) === Number(selectedDuration));

  return (
    <>
      <Header />
      <ToastContainer
        position="top-right"
        autoClose={4000}
        transition={Zoom}
        theme="colored"
      />

      <div className="mapping-container">
        <h1>Operator Plan Mapping {isAdmin && <span className="admin-badge">(Admin)</span>}</h1>

      
        {loading ? (
          <p>Loading mapped plans...</p>
        ) : selectedOperator ? (
          <>
            {/* Bulk Actions */}
            <div className="bulk-actions">
              <div className="bulk-selection">
                <div className="bulk-btn-group">
                  <button 
                    className="bulk-btn select-all-btn"
                    onClick={handleSelectAll}
                    disabled={allPlansSelected}
                  >
                    Select All
                  </button>
                  <button 
                    className="bulk-btn deselect-all-btn"
                    onClick={handleDeselectAll}
                  >
                    Deselect All
                  </button>
                </div>
                <span className="selected-count">
                  {Object.keys(mappedPlans).length} of {plans.length} plans selected
                </span>
              </div>

              <div className="filter-section-pack">
                {['All', 30, 90, 180, 365].map(duration => (
                  <button
                    key={duration}
                    className={`filter-button-pack ${selectedDuration === duration ? 'active' : ''}`}
                    onClick={() => setSelectedDuration(duration)}
                  >
                    {duration === 'All' ? 'All Packages' : 
                      duration === 30 ? 'Monthly' :
                      duration === 90 ? 'Quarterly' :
                      duration === 180 ? 'Half Yearly' : 'Annual'}
                  </button>
                ))}
              </div>
            </div>

            <div className="mapping-table-container">
              <table className="mapping-table">
                <thead>
                  <tr>
                    <th>Assign</th>
                    <th>Plan Name</th>
                    <th>Type</th>
                    <th>Base Price</th>
                    <th>Custom Price</th>
                    <th>Difference</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPackages.map((plan) => {
                    const isSelected = !!mappedPlans[plan.id];
                    const customPrice = isSelected ? mappedPlans[plan.id].price : "";
                    const basePrice = parseFloat(plan.planPrice);
                    const difference = customPrice ? (parseFloat(customPrice) - basePrice) : 0;
                    
                    return (
                      <React.Fragment key={plan.id}>
                        <tr className={isSelected ? "selected" : ""}>
                          <td>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => togglePlan(plan.id)}
                            />
                          </td>
                          <td>
                            <div className="plan-name">{plan.planName}</div>
                          </td>
                          <td>
                            <span className={`plan-type ${plan.planType}`}>
                              {plan.planType}
                            </span>
                          </td>
                          <td>₹{plan.planPrice}</td>
                          <td>
                            {isSelected ? (
                              <div className="price-input-container">
                                <input
                                  type="number"
                                  placeholder="Enter custom price"
                                  value={customPrice}
                                  onChange={(e) =>
                                    handlePriceChange(plan.id, e.target.value)
                                  }
                                  min="0"
                                  step="0.01"
                                />
                                {/* {isAdmin && customPrice && (
                                  <div className="auto-fill-indicator" title="Auto-filled price">
                                    ⚡
                                  </div>
                                )} */}
                              </div>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td>
                            {isSelected && customPrice ? (
                              <span className={`price-difference ${difference >= 0 ? 'positive' : 'negative'}`}>
                                {difference >= 0 ? '+' : ''}₹{difference}
                              </span>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td>
                            <button
                              className="expand-btn"
                              onClick={() => toggleRowExpansion(plan.id)}
                              title={expandedRows.has(plan.id) ? "Hide OTT platforms" : "Show OTT platforms"}
                            >
                              {expandedRows.has(plan.id) ? "−" : "+"}
                            </button>
                          </td>
                          
                        </tr>
                        {expandedRows.has(plan.id) && plan.ott && Array.isArray(plan.ott) && (
                          <tr className="ott-expansion-row">
                            <td colSpan="7">
                              <div className="ott-platforms-expanded">
                                <h4>OTT Platforms Included:</h4>
                                <div className="ott-grid">
                                  {plan.ott.map((eachOtt, index) => {
                                    const lowerOtt = eachOtt.toLowerCase().replace(/\s+/g, '-');
                                    return (
                                      <div key={index} className="ott-platform-item">
                                        <img 
                                          src={`https://vrplay.in/images/${lowerOtt}.png`} 
                                          alt={eachOtt}
                                          className="ott-image"
                                          
                                        />
                                        <span className="ott-platform-name">{eachOtt}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="mapping-actions">
              <button
                className="save-btn"
                onClick={handleSaveMapping}
                disabled={saving || Object.keys(mappedPlans).length === 0}
              >
                {saving ? "Saving..." : `Save Mapping (${Object.keys(mappedPlans).length})`}
              </button>
            </div>
          </>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <p>Please select an operator to manage plan mappings.</p>
          </div>
        )}
      </div>
    </>
  );
};

export default OperatorPlanMapping;